/* eslint-disable @typescript-eslint/no-explicit-any */
import { Tick } from 'chart.js'
import { format } from 'date-fns'
import { flatten } from 'lodash'

import botsIcon from './assets/bots.png'
import engagementsIcon from './assets/engagements.png'
import exclamationImage from './assets/Exclamation.png'
import alertImage from './assets/SurgeAlert.png'
import surgeAlertImage from './assets/SurgeAlert2.png'
import trendingDownIcon from './assets/trending_down.png'
import trendingDownGreenIcon from './assets/trending_down_green.png'
import trendingNeutralIcon from './assets/trending_neutral.png'
import trendingUpIcon from './assets/trending_up.png'
import trendingUpRedIcon from './assets/trending_up_red.png'
import { LineChartDataset } from './Charts.types'

export const convertToAmPm = (hour: number) => {
  if (hour == 0) return '12AM'
  if (hour < 12) return `${hour}AM`
  if (hour == 12) return '12PM'

  return `${hour - 12}PM`
}

export const buildXAxisHourNumbersLabels = (hours: number[]) => {
  const ticks = hours.map((hour) => ({ value: hour, label: convertToAmPm(hour) }))
  return [ticks[0], ticks[Math.floor(ticks.length / 2)], ticks[ticks.length - 1]]
}

export const buildXAxisHourLabels = (labels: Date[], spread: number, timeFormat: '12h' | '24h' = '12h') => {
  const ticks = labels.map((label) => ({
    value: label.getTime(),
    label: format(label, timeFormat === '24h' ? 'HH:mm' : 'hh aa'),
  }))

  const returnTicks = ticks.filter((_, index) => {
    const isLast = index === ticks.length - 1
    return index % spread === 0 || isLast
  })

  return returnTicks
}

const isDateValid = (date: Date) => {
  return date instanceof Date && !Number.isNaN(date.getTime())
}

export const buildXAxisWeeklyLabels = (labels: string[]) => {
  return labels.map((x) => {
    const split = x.split('|')

    const startDate = new Date(split[0].trim())
    const endDate = new Date(split[1].trim())

    if (isDateValid(startDate) && isDateValid(endDate)) {
      const start = format(startDate, 'MM/dd')
      const end = format(endDate, 'MM/dd')

      return `${start} - ${end}`
    } else return ''
  })
}

export const buildXAxisLabels = (
  labels: Date[] | number[] | string[],
  options?: { qtd?: number; format?: string }
) => {
  const ticks = (labels as any[])
    .filter((_: number | Date | string, index) => {
      const isLast = index === labels.length - 1
      return index % (options?.qtd ?? 10) === 0 || isLast
    })
    .map((label: number | Date | string): Tick => {
      if (typeof label === 'number') return { value: label, label: label.toString() }
      if (typeof label === 'string') {
        const date = new Date(label)
        if (isDateValid(date))
          return { value: date.getTime(), label: format(date, options?.format ?? 'MM/dd') }

        return { value: Number.parseInt(label, 10), label: label }
      }

      const formattedLabel = format(label, options?.format ?? 'MM/dd')
      return { value: label.getTime(), label: formattedLabel }
    })

  if (ticks.length > 4) {
    return [...ticks.slice(0, 3), ticks[ticks.length - 1]]
  }
  return ticks
}

export const getTicksForYAxisIgnoringNulls = (dataset: LineChartDataset) => {
  const nonNullValues = dataset.data.filter(
    (v): v is number => v !== null && v !== undefined && !Number.isNaN(v)
  )

  const max = Math.max(...nonNullValues)
  const min = Math.min(...nonNullValues)
  const sum = nonNullValues.reduce((prev, curr) => prev + curr, 0)

  let mean = sum / nonNullValues.length
  mean = Number.isInteger(mean) ? mean : Number(mean.toFixed(2))
  const ticks = [min, mean, max]
  return ticks.map((v) => ({ value: v }))
}

export const buildYAxisLabels = (dataset: LineChartDataset) => {
  const values = calculateYAxisValues(dataset)

  return values.map((v) => ({ value: v }))
}

const uniq = (array: number[]) => [...new Set(array)]

export const calculateYAxisValues = (dataset: LineChartDataset): number[] => {
  const [sum, proposedMax] = findMaxAndSum(dataset.data)

  const safePreferred = dataset?.preferredMax ? dataset.preferredMax : 0
  const max = safePreferred > proposedMax ? safePreferred : proposedMax

  return uniq([0, Math.floor(sum / dataset.data.length || 1), max])
}

export const findMaxAndSum = (data: (number | null)[]) => {
  return data.reduce(
    (prev, curr) => {
      const [sum, max] = prev

      return [sum + (curr ?? 0), (curr ?? 0) > max ? (curr ?? 0) : max]
    },
    [0, 0]
  )
}

export const buildMultilineYAxisLabels = (datasets: LineChartDataset[]) => {
  const maxDataset: number[] = datasets[0].data.map((_, i: number) => {
    const values = datasets.map((d) => d.data[i]).map((v) => (v === null ? 0 : v))
    return Math.max(...values)
  })

  const values = calculateYAxisValues({ data: maxDataset })
  return values.map((v) => ({ value: v }))
}

export const createPredictionLineStyle = (
  labels: number[],
  options: {
    predictionPointReference: number
    alertsPointsReference?: number[]
  }
) => {
  const findPredictionRef = (index: number, value: unknown, defaultValue: unknown) =>
    options.predictionPointReference >= labels[index] ? defaultValue : value

  const findAlertRef = (index: number, value: unknown, defaultValue: unknown) => {
    if (options.alertsPointsReference && options.alertsPointsReference.length > 0)
      if (options.alertsPointsReference.includes(labels[index])) return value

    return defaultValue
  }

  const image = new Image(23, 23)
  image.src = alertImage

  return {
    segment: {
      borderDash: (ctx: { p0DataIndex: number }) => findPredictionRef(ctx.p0DataIndex + 1, [6, 6], undefined),
      borderColor: (ctx: { p0DataIndex: number }) =>
        findPredictionRef(ctx.p0DataIndex + 1, '#D1D5DB', '#589FFB'),
    },
    pointStyle: (ctx: { dataIndex: number }) => findAlertRef(ctx.dataIndex, [image], 'circle'),
    pointBackgroundColor: (ctx: { dataIndex: number }) =>
      findPredictionRef(ctx.dataIndex, '#9CA3AF', '#2E87FA'),
  }
}

export const getOrCreateTooltip = (chart: any) => {
  let tooltipEl = chart.canvas.parentNode.querySelector('div.tooltipCustom')

  if (!tooltipEl) {
    tooltipEl = document.createElement('div')
    tooltipEl.classList.add('tooltipCustom')
    tooltipEl.style.opacity = 1
    tooltipEl.style.pointerEvents = 'none'
    tooltipEl.style.transform = 'translate(-50%, 0)'
    const table = document.createElement('table')
    table.style.margin = '0px'
    tooltipEl.style.zIndex = '9999999'
    tooltipEl.appendChild(table)
    chart.canvas.parentNode.appendChild(tooltipEl)
  }

  return tooltipEl
}

export const externalTooltipHandler = (
  context: any,
  options?: {
    customTotalTitle?: string
    customTotalPredictionTitle?: string
    noBodyLabelEllipsis?: boolean
    moveFirstBar?: number
    reverseTrendingComparison?: boolean
  }
) => {
  const addTitle = (title: string, head: HTMLTableSectionElement) => {
    const tr = document.createElement('tr')
    const th = document.createElement('th')
    const text = document.createTextNode(title)
    th.classList.add('title')
    th.appendChild(text)
    tr.appendChild(th)
    head.appendChild(tr)
  }

  const addTotal = (
    title: string,
    percentage: string | null,
    status: 'positive' | 'negative' | 'neutral',
    head: HTMLTableSectionElement,
    isPrediction?: boolean,
    tasksCount?: number
  ) => {
    const tr = document.createElement('tr')
    const th = document.createElement('th')
    const span = document.createElement('span')
    const p = document.createElement('p')
    const image = document.createElement('img')

    const textColor = options?.reverseTrendingComparison
      ? status === 'positive'
        ? 'negative'
        : 'positive'
      : status

    th.classList.add('total')
    p.classList.add('total-text')
    span.classList.add('total-span', textColor)

    p.style.marginRight = '4px'

    const titleLegend = isPrediction ? options?.customTotalPredictionTitle : options?.customTotalTitle
    const text = document.createTextNode(`${titleLegend ?? 'Total'}: ${title}`)

    const imageDic = {
      neutral: trendingNeutralIcon,
      positive: trendingUpIcon,
      negative: trendingDownIcon,
    }

    const imageReverseDic = {
      neutral: trendingNeutralIcon,
      positive: trendingUpRedIcon,
      negative: trendingDownGreenIcon,
    }

    image.src = options?.reverseTrendingComparison ? imageReverseDic[status] : imageDic[status]

    image.style.marginRight = '4px'

    p.appendChild(text)
    span.appendChild(document.createTextNode(`${percentage}%`))
    th.appendChild(p)
    th.appendChild(image)
    th.appendChild(span)
    tr.appendChild(th)
    head.appendChild(tr)

    // Add tasks to chart tooltip only if tasksCount > 0
    if (tasksCount !== undefined && tasksCount !== null && tasksCount > 0) {
      const volumeTr = document.createElement('tr')
      const volumeTh = document.createElement('th')
      const volumeP = document.createElement('p')

      volumeTh.classList.add('total')
      volumeP.classList.add('total-text')
      volumeP.style.marginTop = '4px'
      volumeP.style.display = 'flex'
      volumeP.style.alignItems = 'center'

      const colorBox = document.createElement('div')
      colorBox.style.width = '8px'
      colorBox.style.height = '8px'
      colorBox.style.borderRadius = '2px'
      colorBox.style.backgroundColor = '#C85F80'
      colorBox.style.marginRight = '4px'
      colorBox.style.flexShrink = '0'

      const volumeText = document.createTextNode(`Tasks: ${tasksCount}`)
      volumeP.appendChild(colorBox)
      volumeP.appendChild(volumeText)
      volumeTh.appendChild(volumeP)
      volumeTr.appendChild(volumeTh)
      head.appendChild(volumeTr)
    }
  }

  const addBody = (
    lines: {
      label: string
      color?: string
      iconName?: string
      values: { type: string; currentValue: number }[]
    }[],
    body: HTMLTableSectionElement
  ) => {
    const maxEachColumn: number[] = []

    lines.forEach((x) => {
      x.values.forEach((y, index) => {
        if (!maxEachColumn[index] || maxEachColumn[index] < y.currentValue) {
          maxEachColumn[index] = y.currentValue
        }
      })
    })

    lines.forEach((line) => {
      const tr = document.createElement('tr')
      const td = document.createElement('td')

      td.style.display = 'flex'

      if (line.values.length > 0) td.style.justifyContent = 'space-between'

      td.style.alignItems = 'center'

      if (line.color) {
        const colorBox = document.createElement('div')
        colorBox.style.width = '8px'
        colorBox.style.height = '8px'
        colorBox.style.borderRadius = '2px'
        colorBox.style.backgroundColor = line.color
        colorBox.style.marginRight = '4px'
        colorBox.style.flexShrink = '0'
        td.appendChild(colorBox)
      }

      if (line.iconName) {
        const image = document.createElement('img')
        image.src = line.iconName === 'surge-alert' ? surgeAlertImage : exclamationImage
        image.style.marginRight = '4px'
        td.appendChild(image)
      }

      const label = document.createElement('label')
      label.appendChild(document.createTextNode(line.label))

      if (!options?.noBodyLabelEllipsis) {
        label.style.whiteSpace = 'nowrap'
        label.style.maxWidth = '120px'
        label.style.overflow = 'hidden'
        label.style.textOverflow = 'ellipsis'
      }

      const values = document.createElement('div')
      values.style.display = 'flex'
      values.style.minWidth = '80px'

      values.style.justifyContent = 'space-between'

      line.values.forEach((value, idx) => {
        const container = document.createElement('div')
        container.style.display = 'flex'
        container.style.gap = '4px'
        container.style.width = '200px'
        container.style.marginLeft = '10px'

        if (!options?.noBodyLabelEllipsis) {
          const maxValueColumnLength = maxEachColumn[idx].toString().length

          if (maxValueColumnLength < 3) {
            container.style.width = '50px'
          } else if (maxValueColumnLength < 5) {
            container.style.width = '70px'
          }
        }

        const text = document.createElement('p')
        text.innerText = value.currentValue.toString()

        const image = document.createElement('img')
        image.style.marginBottom = '-4px'

        if (value.type === 'engagements') {
          image.src = engagementsIcon
        } else if (value.type === 'bots') {
          image.src = botsIcon
        }

        container.appendChild(image)
        container.appendChild(text)
        values.appendChild(container)
      })

      td.appendChild(label)
      td.appendChild(values)
      tr.appendChild(td)
      body.appendChild(tr)
    })
  }

  // Tooltip Element
  const { chart, tooltip } = context
  const tooltipEl = getOrCreateTooltip(chart)

  // Hide if no tooltip
  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = 0
    return
  }

  if (tooltip.body) {
    const tableHead = document.createElement('thead')
    const tableBody = document.createElement('tbody')

    addTitle(tooltip.title[0], tableHead)
    addTotal(
      tooltip.beforeBody[0],
      tooltip.beforeBody[1],
      tooltip.beforeBody[2],
      tableHead,
      tooltip.beforeBody[3],
      tooltip.beforeBody[4]
    )
    addBody(
      tooltip.body
        .map(
          (b: { lines: { label: string; values: { type: string; currentValue: number }[] }[] }) => b.lines
        )[0]
        .filter((line: { label: string }) => !line.label.toLowerCase().includes('tasks')),
      tableBody
    )

    const tableRoot = tooltipEl.querySelector('table')

    while (tableRoot.firstChild) {
      tableRoot.firstChild.remove()
    }

    tableRoot.appendChild(tableHead)
    tableRoot.appendChild(tableBody)
  }

  const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas

  const isFirstBar = tooltip.dataPoints && tooltip.dataPoints[0]?.dataIndex === 0

  tooltipEl.style.opacity = 1
  tooltipEl.style.left = positionX + tooltip.caretX + (isFirstBar ? (options?.moveFirstBar ?? 0) : 0) + 'px'
  tooltipEl.style.top = positionY + tooltip.caretY + 'px'
}

export const setMinYScale = (config: any, datasets: any[], options?: { min?: number; max?: number }) => {
  const newConfig = { ...config }
  const mergedArray = flatten(datasets?.map((dataset) => dataset.data)).filter((x) => x !== null) as number[]

  if (mergedArray.length === 0) {
    newConfig.scales.y.min = 0
    newConfig.scales.y.max = 0
    return newConfig
  }

  newConfig.scales.y.min = options?.min ?? Math.min(...mergedArray)
  newConfig.scales.y.max = options?.max ?? Math.max(...mergedArray)

  return newConfig
}
